import React from 'react';

import type { MiddlewhereFilter } from '../../../filter/FilterItemManager';
/* eslint-disable-next-line import/no-cycle */
import usePagination, { type PaginationProps, type PaginationType } from '../../../hooks/usePagination';
import useSettings from '../../../hooks/useSettings';
import useLocalStorage from '../../../hooks/useLocalStorage';

export interface ViewState {
	filters?: MiddlewhereFilter[];
	orderBy?: string;
	orderOperator?: string;
}

export interface ViewStateType {
	paginationProps: PaginationType;
}

type ViewStateProps = PaginationProps;

export default function useViewState(props: ViewStateProps) {
	const { settings } = useSettings(props.module);

	const [viewState, setViewState] = useLocalStorage<ViewState | undefined>(props.id, undefined);

	const handleStateChange = React.useCallback(
		(key: string, newValue: any) => {
			setViewState(state => {
				const newState = state ? state : {};
				newState[key] = newValue;
				return { ...newState };
			});
		},
		[setViewState]
	);

	const { filterProps, orderProps, paginatorProps } = usePagination({
		id: props.id,
		module: props.module,
		filter: {
			defaultFilters: [
				...(props.filter?.defaultFilters || []),
				{ filt_field: `${settings.modu_short}_deleted`, filt_clause: 'not_equal', filt_value: 1 }
			],
			defaultQuery: props.filter?.defaultQuery,
			filters: viewState?.filters || props.filter?.filters
		},
		order: {
			orderBy: viewState?.orderBy || props.order?.orderBy,
			orderOperator: viewState?.orderOperator || props.order?.orderOperator,
			setOrderBy: props.order?.setOrderBy,
			setOrderOperator: props.order?.setOrderOperator
		},
		paginator: props.paginator
	});

	return {
		paginationProps: {
			filterProps: {
				...filterProps,
				handleFiltersApply: () => {
					const newFilters = filterProps.handleFiltersApply();
					handleStateChange('filters', newFilters);
				},
				handleFiltersClear: () => {
					filterProps.handleFiltersClear();
					handleStateChange('filters', []);
				}
			},
			orderProps: {
				...orderProps,
				setOrderBy: newValue => {
					orderProps.setOrderBy(newValue);
					handleStateChange('orderBy', newValue);
				},
				setOrderOperator: newValue => {
					orderProps.setOrderBy(newValue);
					handleStateChange('orderOperator', newValue);
				}
			},
			paginatorProps
		}
	} as ViewStateType;
}
